import { scheduleOnce } from '@ember/runloop';
import EmberObject from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@ember/component';
import { initializePropertyDefaults } from 'sh-www/utils/initialize-state-properties';

/**
 * Base component for a modal in the LSG Dialog style.
 */
const LSGModalBase = Component.extend({
  tagName: '',

  /**
   * Register Services
   */
  errorTracker: service('error-tracker'),

  /**
   * The main model for the Modal
   *
   * @type {Object|null}
   */
  model: null,

  /**
   * This is where you can define defaults for the 'model' that is passed in by the caller.  These will all be reset
   * automagically each time the popover closes.
   *
   * Children should override this with any properties on the model.* that you would want reset.
   *
   * @type {Object}
   */
  propertyDefaults: {},

  /**
   * This is where you can define defaults for the internal 'state' that is private to the component.
   * These will all be reset automagically each time the popover closes.
   *
   * Children should override this with any internal state properties that you would want reset.
   *
   * @type {Object}
   */
  stateDefaults: {},

  /**
   * This will be set to True after each time the modal has been opened and initialized.
   *
   * @type {Boolean}
   * @private
   */
  _isInitialized: false,

  /**
   * Child can implement to set a callback when the modal closes.
   *
   * @protected
   */
  _onClose() {},

  /**
   * In general, you want to use _onClose, but you can use this method if you want to do something
   * and you need to ensure that the state of the popover has not been tore down yet.
   *
   * @protected
   */
  _onCloseBeforeModelCleared() {},

  /**
   * Called when we initialize the modal before the default props and the state are initialized.
   *
   * @deprecated - Use _onInitializationComplete instead.
   * @protected
   */
  _onInitialization() {},

  /**
   * Called after the initialization of the base module is done.
   *
   * @protected
   */
  _onInitializationComplete() {},

  /**
   * Called after the modal has been rendered.
   */
  _onModalRenderComplete() {},

  /**
   * This will reset our internal state back to the default when popover is initialized.
   *
   * @protected
   */
  _resetToDefaultState() {
    let defaultState = Object.assign({}, initializePropertyDefaults(this, true, 'stateDefaults'));
    this.set('state', EmberObject.create(defaultState));
  },

  init() {
    this._super();

    if (!this.get('model') || this.get('_isInitialized')) {
      return;
    }

    this.set('_isInitialized', true);
    this._onInitialization();

    let modelWithDefaults = Object.assign({}, initializePropertyDefaults(this, true), this.get('model'));
    this.set('model', modelWithDefaults);

    this._resetToDefaultState();
    this.set('errors', {});

    this._onInitializationComplete();
  },

  didInsertElement() {
    this._super(...arguments);

    scheduleOnce('afterRender', this, function() {
      this._onModalRenderComplete();
    });
  },

  willDestroy() {
    this._super();

    this._onCloseBeforeModelCleared();
    this.set('model', null);
    this.set('_isInitialized', false);
    this._onClose();
  },
});

export default LSGModalBase;
